import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import {
  Button,
  Grid,
  Header2Text,
  IconType,
  ISpruceFieldProps,
  NavigationItem,
  RotatingIcon,
  SideSheet,
  SpruceField,
} from 'ui-toolkit';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { IPresentationDefaultContact } from '../../types/clients/IDefaultContact';
import { DefaultContactRolesField } from './DefaultContactRolesField';
import GrayLine from '../shared/GrayLine';
import { editDefaultContactFormWithDialog } from '../../validation/clients/DefaultContactForm';
import { IAddEditDefaultContactData } from '../../types/clients/CreateDefaultContact';
import { DefaultContactEditOrdersDialog } from './DefaultContactEditOrdersDialog';
import { EditContactFormStatusWrapper } from '../order/contacts/subComponents/EditContactFormStatusWrapper';
import { OrderContactNotesField } from '../order/contacts/subComponents/OrderContactNotesField';

interface IEditDefaultContactFormProps {
  onSubmit: (value: any) => void;
  getAffectedOrders: (value: any) => void;
  onCancel: () => void;
  initialData: IPresentationDefaultContact;
  clientId: string;
  deleteFetchError?: Error;
  deleteFetchStatus: IFetchStatus;
  updateFetchError?: Error;
  updateFetchStatus: IFetchStatus;
  affectedOrderFetchStatus: IFetchStatus;
  affectedOrders: number;
}

export const EditDefaultContactForm: React.FC<IEditDefaultContactFormProps> = ({
                                                                                 onSubmit,
                                                                                 onCancel,
                                                                                 getAffectedOrders,
                                                                                 affectedOrderFetchStatus,
                                                                                 initialData,
                                                                                 deleteFetchError,
                                                                                 deleteFetchStatus,
                                                                                 updateFetchError,
                                                                                 updateFetchStatus,
                                                                                 clientId,
                                                                                 affectedOrders,
                                                                               }) => {
  const [showEditOrdersDialog, setShowEditOrdersDialog] = useState(false);

  const formInitialData: Partial<IAddEditDefaultContactData> = {
    id: initialData.id,
    roles: initialData.roles,
    adminId: initialData.admin.id,
    clientCompanyId: clientId,
    notes: initialData.notes,
  };
  const onSubmitClick = (formik: ISpruceFieldProps) => {
    if (formik.formik.touched['roles']) {
      formik.field.onChange('UPDATE');
      setShowEditOrdersDialog(true);
    } else {
      onSubmit({ ...formik.formik.values, updateType: 'UPDATE', updateOrders: false });
    }
  };

  return (
    <Formik
      initialValues={formInitialData}
      validateOnMount={true}
      validationSchema={editDefaultContactFormWithDialog()}
      onSubmit={(values) => {
        onSubmit(values);
        setShowEditOrdersDialog(false);
      }}
      onReset={onCancel}
    >
      {({ isValid, dirty, handleSubmit, values }) => (
        <Form onSubmit={handleSubmit}>
          <SideSheet
            title={'Edit Contact'}
            onClose={onCancel}
            footer={
              <>
                {showEditOrdersDialog ? (
                  <>
                    {values['updateOrders'] && (
                      <Grid columnCount={2}>
                        <Button intent={'secondary'} type={'reset'}>
                          Cancel
                        </Button>
                        <Button
                          intent={'primary'}
                          type={'submit'}
                          disabled={!isValid || !dirty || updateFetchStatus.fetching}
                        >
                          Save
                        </Button>
                      </Grid>
                    )}
                  </>
                ) : (
                  <SpruceField name={'updateType'}>
                    {(formik: ISpruceFieldProps) => (
                      <Grid>
                        <Button
                          intent={'primary'}
                          type={'button'}
                          onClick={() => onSubmitClick(formik)}
                          disabled={!isValid || !dirty || updateFetchStatus.fetching}
                        >
                          Save and Update
                        </Button>
                        <Grid columnCount={2}>
                          <Button intent={'secondary'} type={'reset'}>
                            Cancel
                          </Button>
                          <Button
                            intent={'danger'}
                            type={'button'}
                            onClick={() => {
                              formik.field.onChange('DELETE');
                              setShowEditOrdersDialog(true);
                            }}
                          >
                            Delete Contact
                          </Button>
                        </Grid>
                      </Grid>
                    )}
                  </SpruceField>
                )}
              </>
            }
          >
            <Grid>
              <EditContactFormStatusWrapper
                fetching={updateFetchStatus.fetching || deleteFetchStatus.fetching}
                fetchError={updateFetchError || deleteFetchError}
                updateFetchedSuccess={updateFetchStatus.success}
                updateSuccessMessage={`Contact Updated! ${affectedOrders} order(s) updated`}
                deleteFetchedSuccess={deleteFetchStatus.success}
                deleteSuccessMessage={`${affectedOrders} order(s) deleted`}
                onResetDeleteAndCancel={onCancel}
              />
              {showEditOrdersDialog ? (
                <DefaultContactEditOrdersDialog
                  onNoClick={handleSubmit}
                  getAffectedOrders={getAffectedOrders}
                  preliminaryAffectedOrderFetchStatus={affectedOrderFetchStatus}
                />
              ) : (
                <>
                  <div>
                    <Header2Text>{initialData.admin.fullName}</Header2Text>
                    <NavigationItem
                      href={`mailto:${initialData.admin.email}`}
                      text={initialData.admin.email}
                      isActive
                      isInline
                    />
                  </div>
                  <OrderContactNotesField name={'notes'} />
                  <GrayLine />
                  <DefaultContactRolesField isValid={isValid} />
                </>
              )}
              {affectedOrderFetchStatus.success && values['updateOrders'] && (
                <>{affectedOrders} order(s) will be affected</>
              )}
              {affectedOrderFetchStatus.fetching && <RotatingIcon type={IconType.Loading} />}
            </Grid>
          </SideSheet>
        </Form>
      )}
    </Formik>
  );
};
